import type { FC } from 'react';
import { Box, Button, Container, Stack, SvgIcon, Typography } from '@mui/material';
import ArrowUpIcon from '@mui/icons-material/ArrowUpward';
import { RouterLink } from 'src/components/router-link';
import { paths } from 'src/paths';

interface Section {
  title: string;
  items: {
    external?: boolean;
    title: string;
    path: string;
  }[];
}

const sections: Section[] = [
  {
    title: 'Studio',
    items: [
      { title: 'Home', path: paths.index },
      { title: 'Contact', path: paths.contact },
      { title: 'Support', path: paths.support }
    ]
  },
  {
    title: 'Services',
    items: [
      { title: 'Branding', path: paths.contact },
      { title: 'Websites', path: paths.contact },
      { title: 'Marketing', path: paths.contact },
      { title: 'Content', path: paths.contact }
    ]
  },
  {
    title: 'Social',
    items: [
      { title: 'YouTube', path: 'https://www.youtube.com/MysticMediaFilm', external: true },
      { title: 'Instagram', path: 'https://www.instagram.com/MysticMediaFilm/', external: true },
      { title: 'X', path: 'https://twitter.com/MysticMediaFilm', external: true },
      { title: 'Facebook', path: 'https://www.facebook.com/MysticMediaFilm/', external: true },
      { title: 'LinkedIn', path: 'https://www.linkedin.com/in/mysticmediafilm', external: true },
      { title: 'Discord', path: 'https://discord.gg/a9XGNEDBrT', external: true }
    ]
  },
  {
    title: 'Legal',
    items: [
      { title: 'Privacy', path: paths.privacyPolicy },
      { title: 'Terms', path: paths.eula },
      { title: 'Refund Policy', path: paths.refundPolicy }
    ]
  }
];

// Returns the current booking window label, e.g. "Booking Q2 2026".
// Updates automatically as time rolls forward — no manual edits needed each quarter or new year.
const getBookingLabel = (): string => {
  const now = new Date();
  const quarter = Math.floor(now.getMonth() / 3) + 1; // Jan-Mar = Q1, Apr-Jun = Q2, etc.
  return `Booking Q${quarter} ${now.getFullYear()}`;
};

export const Footer: FC = (props) => {
  const handleBackToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const bookingLabel = getBookingLabel();

  return (
    <Box
      sx={{
        position: 'relative',
        background: '#0a0a0b',
        color: '#ffffff',
        pt: { xs: 10, md: 18 },
        pb: { xs: 5, md: 7 },
        mt: { xs: -3, md: -6 },
        overflow: 'hidden',
        clipPath: 'polygon(0 4%, 100% 0, 100% 100%, 0 100%)'
      }}
      {...props}
    >
      {/* Blue glows */}
      <Box
        sx={{
          position: 'absolute',
          top: '-20%',
          right: '-10%',
          width: 600,
          height: 600,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(37, 99, 235, 0.28) 0%, transparent 65%)',
          filter: 'blur(80px)',
          pointerEvents: 'none'
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '-30%',
          left: '-10%',
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(99, 102, 241, 0.2) 0%, transparent 65%)',
          filter: 'blur(80px)',
          pointerEvents: 'none'
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Top row: CTA prompt + back to top */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={4}
          sx={{ pb: { xs: 6, md: 8 }, borderBottom: '1px solid rgba(255,255,255,0.08)' }}
        >
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <Box sx={{ width: 24, height: 1, background: 'rgba(255,255,255,0.4)' }} />
              <Typography
                sx={{
                  fontFamily: '"Roboto Mono", monospace',
                  fontSize: 11,
                  fontWeight: 500,
                  color: 'rgba(255,255,255,0.75)',
                  letterSpacing: 1.4,
                  textTransform: 'uppercase'
                }}
              >
                Ready when you are
              </Typography>
            </Stack>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: { xs: '32px', md: '48px' },
                fontWeight: 700,
                lineHeight: 1.04,
                letterSpacing: '-0.035em',
                color: '#ffffff',
                maxWidth: 640
              }}
            >
              Have a project in mind?{' '}
              <Box
                component={RouterLink}
                href={paths.contact}
                sx={{
                  color: '#60a5fa',
                  textDecoration: 'none',
                  borderBottom: '2px solid rgba(96, 165, 250, 0.3)',
                  transition: 'border-color 0.2s ease',
                  '&:hover': { borderBottomColor: '#60a5fa' }
                }}
              >
                Let&apos;s talk.
              </Box>
            </Typography>
          </Stack>

          <Button
            onClick={handleBackToTop}
            endIcon={(
              <SvgIcon sx={{ fontSize: 16 }}>
                <ArrowUpIcon />
              </SvgIcon>
            )}
            sx={{
              fontFamily: '"Inter", sans-serif',
              color: '#ffffff',
              fontWeight: 500,
              fontSize: 14,
              letterSpacing: '-0.005em',
              px: 2.5,
              py: 1.25,
              borderRadius: 999,
              textTransform: 'none',
              border: '1px solid rgba(255,255,255,0.15)',
              background: 'transparent',
              '&:hover': { background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.35)' }
            }}
          >
            Back to top
          </Button>
        </Stack>

        {/* Main grid: brand + link columns */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: '1.8fr repeat(4, 1fr)' },
            gap: { xs: 5, md: 4 },
            py: { xs: 6, md: 8 }
          }}
        >
          {/* Brand column */}
          <Stack spacing={3} sx={{ maxWidth: 360 }}>
            <Stack
              alignItems="center"
              component={RouterLink}
              direction="row"
              display="inline-flex"
              href={paths.index}
              spacing={1.25}
              sx={{ textDecoration: 'none' }}
            >
              <Box
                component="img"
                src="/assets/logo-main.png"
                alt="Mystic Media Film"
                sx={{ height: 30, width: 'auto', objectFit: 'contain' }}
              />
              <Typography
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontSize: 18,
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  color: '#ffffff'
                }}
              >
                Mystic Media Film
              </Typography>
            </Stack>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: 14,
                fontWeight: 400,
                lineHeight: 1.55,
                color: 'rgba(255,255,255,0.55)',
                maxWidth: 320
              }}
            >
              A full-service digital studio based in Katy, TX. Branding, websites,
              and marketing built for ambitious brands.
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  background: '#22c55e',
                  boxShadow: '0 0 8px rgba(34, 197, 94, 0.6)'
                }}
              />
              <Typography
                sx={{
                  fontFamily: '"Roboto Mono", monospace',
                  fontSize: 11,
                  fontWeight: 500,
                  color: 'rgba(255,255,255,0.7)',
                  letterSpacing: 0.8,
                  textTransform: 'uppercase'
                }}
              >
                {bookingLabel}
              </Typography>
            </Stack>
          </Stack>

          {/* Link columns */}
          {sections.map((section) => (
            <Stack key={section.title} spacing={2}>
              <Typography
                sx={{
                  fontFamily: '"Roboto Mono", monospace',
                  fontSize: 11,
                  fontWeight: 500,
                  color: 'rgba(255,255,255,0.5)',
                  letterSpacing: 1.2,
                  textTransform: 'uppercase'
                }}
              >
                {section.title}
              </Typography>
              <Stack spacing={1.25}>
                {section.items.map((item) => {
                  const linkProps: any = item.external
                    ? { component: 'a', href: item.path, target: '_blank', rel: 'noopener noreferrer' }
                    : { component: RouterLink, href: item.path };
                  return (
                    <Box
                      key={item.title}
                      {...linkProps}
                      sx={{
                        fontFamily: '"Inter", sans-serif',
                        fontSize: 14,
                        fontWeight: 500,
                        color: '#ffffff',
                        textDecoration: 'none',
                        letterSpacing: '-0.005em',
                        transition: 'color 0.15s ease',
                        '&:hover': { color: '#60a5fa' }
                      }}
                    >
                      {item.title}
                    </Box>
                  );
                })}
              </Stack>
            </Stack>
          ))}
        </Box>

        {/* Giant wordmark */}
        <Box sx={{ py: { xs: 3, md: 5 } }}>
          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontSize: { xs: '64px', sm: '120px', md: '180px', lg: '220px' },
              fontWeight: 700,
              lineHeight: 0.9,
              letterSpacing: '-0.06em',
              color: 'transparent',
              WebkitTextStroke: '1px rgba(255,255,255,0.18)',
              textAlign: 'center',
              userSelect: 'none'
            }}
          >
            Mystic Media Film
          </Typography>
        </Box>

        {/* Bottom bar */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
          sx={{ pt: { xs: 4, md: 5 }, borderTop: '1px solid rgba(255,255,255,0.08)' }}
        >
          <Typography
            component={RouterLink}
            href={paths.dashboard.index}
            sx={{
              fontFamily: '"Roboto Mono", monospace',
              fontSize: 11,
              fontWeight: 400,
              color: 'rgba(255,255,255,0.45)',
              letterSpacing: 0.5,
              textDecoration: 'none',
              transition: 'color 0.2s ease',
              '&:hover': { color: 'rgba(255,255,255,0.85)' }
            }}
          >
            © {new Date().getFullYear()} Mystic Media Film, LLC. All rights reserved.
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Roboto Mono", monospace',
              fontSize: 11,
              fontWeight: 400,
              color: 'rgba(255,255,255,0.45)',
              letterSpacing: 0.5,
              cursor: 'default',
              transition: 'color 0.2s ease',
              '&:hover': { color: 'rgba(255,255,255,0.85)' }
            }}
          >
            Veteran-owned
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
};
